import type React from "react"
import { User, Edit, Save, X } from "lucide-react"
import { useEffect, useState, useCallback } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Input } from "../ui/input"
import { Label } from "../ui/label"
import { Button } from "../ui/button"

interface AttorneyInformation {
  first_name: string
  last_name: string
  email: string
  phone: string
}

interface PhoneValidation {
  phoneChecking: boolean
  phoneAvailable: boolean | null
  phoneMessage: string
}

interface EmailValidation {
  emailChecking: boolean
  emailAvailable: boolean | null
  emailMessage: string
}


interface AttorneyInformationCardProps {
  attorneyData: AttorneyInformation
  errors?: { [key: string]: string[] }
  onDataChange: (field: keyof AttorneyInformation, value: string) => void
  viewOnly?: boolean
  isEditing?: boolean
  onEdit?: () => void
  onSave?: (data: AttorneyInformation) => void
  onCancel?: () => void
  alwaysEditable?: boolean
  phoneValidation?: PhoneValidation
  emailValidation?: EmailValidation
  onPhoneFormatValidationChange?: (hasError: boolean) => void
}

export default function AttorneyInformationCard({ 
  attorneyData, 
  errors = {}, 
  onDataChange,
  viewOnly = false,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  alwaysEditable = false,
  phoneValidation,
  emailValidation,
  onPhoneFormatValidationChange
  
}: AttorneyInformationCardProps) {
  // State for debounced phone format validation
  const [debouncedPhoneFormatError, setDebouncedPhoneFormatError] = useState<string | null>(null)
  const handleInputChange = (field: keyof AttorneyInformation, value: string) => {
    onDataChange(field, value)
  }

  const getPhoneFormatValidation = useCallback(() => {
    if (!attorneyData.phone || attorneyData.phone.length === 0) return null
    
    const phoneWithoutPlus = attorneyData.phone.startsWith('+') ? attorneyData.phone.slice(1) : attorneyData.phone
    
    // Check if phone has non-numeric characters
    if (!/^\d+$/.test(phoneWithoutPlus)) {
      return "Phone number must contain only numbers (with optional + prefix)"
    }
    
    // Check if phone is too short
    if (phoneWithoutPlus.length < 10) {
      return "Phone number must have at least 10 digits"
    }
    
    // Check if phone is too long
    if (phoneWithoutPlus.length > 15) {
      return "Phone number cannot exceed 15 digits"
    }
    
    return null
  }, [attorneyData.phone])

  // Phone format validation with smart debouncing
  useEffect(() => {
    // Always check current validation first
    const currentValidation = getPhoneFormatValidation()
    
    // If input is empty or valid, clear any existing error immediately
    if (!attorneyData.phone || attorneyData.phone.length === 0 || !currentValidation) {
      setDebouncedPhoneFormatError(null)
      // Notify parent that there's no format error
      if (onPhoneFormatValidationChange) {
        onPhoneFormatValidationChange(false)
      }
      return
    }

    // If current input is invalid, set error after delay
    const timer = setTimeout(() => {
      setDebouncedPhoneFormatError(currentValidation)
      // Notify parent that there is a format error
      if (onPhoneFormatValidationChange) {
        onPhoneFormatValidationChange(true)
      }
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [attorneyData.phone, onPhoneFormatValidationChange, getPhoneFormatValidation])

  const handleSave = () => {
    if (onSave) {
      onSave(attorneyData)
    }
  }

  const getPhoneFieldStyle = () => {
    // Check for debounced format validation errors first
    if (debouncedPhoneFormatError) return "border-red-500"
    
    // Check for general errors
    if (errors.phone) return "border-red-500"
    
    // If no phoneValidation, return default
    if (!phoneValidation) return ""
    
    // Check availability validation states
    if (phoneValidation.phoneChecking) return "border-slate-300"
    if (phoneValidation.phoneAvailable === true) return "border-green-500 focus:border-green-500 focus:ring-green-500"
    if (phoneValidation.phoneAvailable === false) return "border-red-500"
    if (phoneValidation.phoneMessage && phoneValidation.phoneMessage !== "Phone number is available" && phoneValidation.phoneMessage !== "Phone number is already in use by another attorney") {
      return "border-red-500" // Format validation error
    }
    
    return ""
  }

  // Get email field styling based on validation state
  const getEmailFieldStyle = () => {
    if (!emailValidation) return errors.email ? "border-red-500" : ""
    
    if (emailValidation.emailChecking) return "border-slate-300"
    if (emailValidation.emailAvailable === true) return "border-green-500"
    if (emailValidation.emailAvailable === false) return "border-red-500"
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another attorney") {
      return "border-red-500" // Format validation error
    }
    return errors.email ? "border-red-500" : ""
  }

  // Get email validation icon
  const getEmailIcon = () => {
    if (!emailValidation) return null
    
    if (emailValidation.emailChecking) {
      return <div className="h-4 w-4 text-slate-400 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600"></div>
    }
    if (emailValidation.emailAvailable === true) {
      return <div className="h-4 w-4 text-green-500">✓</div>
    }
    if (emailValidation.emailAvailable === false) {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    // Show error icon for format validation errors
    if (emailValidation.emailMessage && emailValidation.emailMessage !== "Email is available" && emailValidation.emailMessage !== "Email is already in use by another attorney") {
      return <div className="h-4 w-4 text-red-500">✗</div>
    }
    return null
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="border-b border-slate-200">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Attorney Information
          </CardTitle>
          {!alwaysEditable && (
            <div className="flex items-center gap-2">
              {viewOnly && !isEditing && onEdit && (
                <Button size="sm" onClick={onEdit} className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {isEditing && (
                <>
                  <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button type="button" size="sm" onClick={handleSave} className="bg-green-600 hover:bg-green-700">
                    <Save className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div className="space-y-2">
            <Label htmlFor="first_name" className="text-sm font-medium text-slate-700">
              First Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="first_name"
                value={attorneyData.first_name}
                onChange={(e) => handleInputChange("first_name", e.target.value)}
                placeholder="Enter first name"
                className={errors.first_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{attorneyData.first_name}</p>
              </div>
            )}
            {errors.first_name && <p className="text-sm text-red-500">{errors.first_name[0]}</p>}
          </div>

          {/* Last Name */}
          <div className="space-y-2">
            <Label htmlFor="last_name" className="text-sm font-medium text-slate-700">
              Last Name *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="last_name"
                value={attorneyData.last_name}
                onChange={(e) => handleInputChange("last_name", e.target.value)}
                placeholder="Enter last name"
                className={errors.last_name ? "border-red-500" : ""}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{attorneyData.last_name}</p>
              </div>
            )}
            {errors.last_name && <p className="text-sm text-red-500">{errors.last_name[0]}</p>}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-slate-700">
              Email *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <div className="relative">
                <Input
                  id="email"
                  value={attorneyData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="Enter email address"
                  className={`pr-10 ${getEmailFieldStyle()}`}
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  {getEmailIcon()}
                </div>
              </div>
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{attorneyData.email}</p>
              </div>
            )}
            {/* Email validation message */}
            {emailValidation && emailValidation.emailMessage && (
              <p className={`text-sm ${
                emailValidation.emailAvailable === true ? 'text-green-600' : 
                emailValidation.emailAvailable === false ? 'text-red-600' : 
                emailValidation.emailMessage.includes('valid') || emailValidation.emailMessage.includes('cannot') ? 'text-red-600' : 
                'text-slate-600'
              }`}>
                {emailValidation.emailChecking ? "Checking email availability..." : emailValidation.emailMessage}
              </p>
            )}
            {errors.email && <p className="text-sm text-red-500">{errors.email[0]}</p>}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-sm font-medium text-slate-700">
              Phone *
            </Label>
            {(isEditing || alwaysEditable) ? (
              <Input
                id="phone"
                value={attorneyData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                placeholder="Enter phone number"
                className={getPhoneFieldStyle()}
              />
            ) : (
              <div className="p-2 bg-slate-50 rounded-md border border-slate-200">
                <p className="text-sm text-slate-900">{attorneyData.phone}</p>
              </div>
            )}
            {/* Phone validation messages - show only one error at a time */}
            {(phoneValidation || debouncedPhoneFormatError || errors.phone) && (isEditing || alwaysEditable) && (
              <div className="space-y-1">
                {/* Priority 1: Format validation errors */}
                {debouncedPhoneFormatError && (
                  <p className="text-sm text-red-600">✗ {debouncedPhoneFormatError}</p>
                )}
                
                {/* Priority 2: Backend errors (only if no format errors) */}
                {!debouncedPhoneFormatError && errors.phone && (
                  <p className="text-sm text-red-600">✗ {errors.phone[0]}</p>
                )}
                
                {/* Priority 3: Availability validation messages (only if no other errors) */}
                {phoneValidation && !debouncedPhoneFormatError && !errors.phone && (
                  <>
                    {phoneValidation.phoneChecking && (
                      <p className="text-sm text-blue-600">Checking phone availability...</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === true && (
                      <p className="text-sm text-green-600">✓ {phoneValidation.phoneMessage}</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === false && (
                      <p className="text-sm text-red-600">✗ {phoneValidation.phoneMessage}</p>
                    )}
                    {!phoneValidation.phoneChecking && phoneValidation.phoneAvailable === null && phoneValidation.phoneMessage && (
                      <p className="text-sm text-amber-600">{phoneValidation.phoneMessage}</p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 